import { ArrowRight } from "lucide-react";
import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { ContentHero } from "@/app/components/content-hero";
import { siteHeaderActions, siteNavItems } from "@/app/content";
import { getOrderedBlogPosts } from "@/app/seo-blog";

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
    d.getUTCDate(),
  ).padStart(2, "0")}`;
}

export default function BlogIndex() {
  const posts = getOrderedBlogPosts();
  return (
    <PageShell>
      <TopNav navItems={siteNavItems} actionItems={siteHeaderActions} homeHref="/" />

      <ContentHero
        eyebrow="BLOG · 长文索引"
        eyebrowColor="#A3E635"
        titleTop="OPC 经济 ·"
        titleBottom="协议设计笔记"
        bottomColor="#A3E635"
        lead="CALL ANYTHING 是一个让 AI Agent 通过标准化 Hotline 协议调用外部能力的开放体系。这页是我们持续输出的长文索引：OPC 商业化、Hotline 协议设计取舍、Marketplace 演进与超级个体经营观察。"
        meta={[`${posts.length} 篇文章`, "RSS 可订阅", "JSON-LD Blog schema"]}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Blog" },
        ]}
      >
        <a
          href="/blog/feed.xml"
          className="inline-flex items-center gap-2 border-2 border-white/40 bg-white/10 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-white hover:bg-white/20"
        >
          RSS Feed
          <ArrowRight className="h-3 w-3" strokeWidth={4} />
        </a>
      </ContentHero>

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid gap-6">
          {posts.map((post) => (
            <article
              key={post.slug}
              className="border-4 border-black bg-white/85 backdrop-blur-sm shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]"
            >
              <a href={post.path} className="block p-6 sm:p-8">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="border-2 border-black bg-black px-2 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#A3E635]">
                    {formatDate(post.date)}
                  </span>
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="border-2 border-black bg-[#A3E635] px-2 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-black"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h2 className="mt-4 text-2xl font-black uppercase text-black sm:text-3xl">
                  {post.title}
                </h2>
                <p className="mt-3 text-sm font-bold leading-7 text-zinc-700 sm:text-base">
                  {post.tldr}
                </p>

                <div className="mt-5 inline-flex items-center gap-2 border-2 border-black bg-black px-3 py-1.5 text-xs font-black uppercase text-white">
                  阅读全文
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={3} />
                </div>
              </a>
            </article>
          ))}
        </div>

        <aside className="mt-14 border-[6px] border-black bg-[#A3E635]/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <h2 className="text-2xl font-black uppercase text-black sm:text-3xl">订阅更新</h2>
          <p className="mt-3 text-sm font-bold leading-6 text-black sm:text-base">
            把 RSS 加进 Feedly / Reeder / Inoreader / Cursor，新文章自动到达。我们也会把每一篇同步到 GitHub Discussions。
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href="/blog/feed.xml"
              className="inline-flex items-center gap-2 border-4 border-black bg-black px-5 py-3 text-sm font-black uppercase text-white shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
            >
              RSS Feed
            </a>
            <a
              href="https://github.com/hejiajiudeeyu/delegated-execution-protocol/discussions"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-4 border-black bg-white px-5 py-3 text-sm font-black uppercase text-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
            >
              GitHub Discussions
            </a>
          </div>
        </aside>
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
